/**
 * SimpleDragManager - Clean cursor delta approach like annotations
 * No complex state transitions, just simple offset calculations
 */

import { create } from 'zustand';

interface SimpleDragState {
  // Drag mode
  isDragging: boolean;
  dragMode: 'single' | 'group' | null;

  // Simple state tracking
  startCursorPos: { x: number; y: number };
  originalPositions: Map<string, { x: number; y: number; assemblyId: string }>;

  // Live editing state (like annotations)
  liveEditingPointId: string | null;
  liveEditingPositions: Map<string, { x: number; y: number }>;

  // Actions
  startDrag: (
    mode: 'single' | 'group',
    points: Array<{ pointId: string; assemblyId: string; x: number; y: number }>,
    cursorPos: { x: number; y: number }
  ) => void;
  updateDrag: (cursorPos: { x: number; y: number }) => void;
  endDrag: () => Array<{ pointId: string; assemblyId: string; finalX: number; finalY: number }>;
  cancelDrag: () => void;

  // Helpers
  getPointPosition: (pointId: string) => { x: number; y: number } | null;
  isDraggingPoint: (pointId: string) => boolean;
  setLiveEditingPoint: (
    pointId: string | null,
    positions?: Map<string, { x: number; y: number }>
  ) => void;
}

export const useSimpleDragManager = create<SimpleDragState>((set, get) => ({
  isDragging: false,
  dragMode: null,
  startCursorPos: { x: 0, y: 0 },
  originalPositions: new Map(),
  liveEditingPointId: null,
  liveEditingPositions: new Map(),

  startDrag: (mode, points, cursorPos) => {
    const originalPositions = new Map<string, { x: number; y: number; assemblyId: string }>();

    points.forEach(point => {
      originalPositions.set(point.pointId, {
        x: point.x,
        y: point.y,
        assemblyId: point.assemblyId,
      });
    });

    set({
      isDragging: true,
      dragMode: mode,
      startCursorPos: { ...cursorPos },
      originalPositions,
      liveEditingPositions: new Map(),
    });

    // Broadcast coordinated drag start event
    try {
      document.dispatchEvent(
        new CustomEvent('coordinated:drag-start', {
          detail: {
            mode,
            draggedPoints: points,
            cursorPos,
          },
        })
      );
    } catch {}
  },

  updateDrag: cursorPos => {
    const state = get();
    if (!state.isDragging) return;

    // Simple delta calculation (same as annotations)
    const totalDx = cursorPos.x - state.startCursorPos.x;
    const totalDy = cursorPos.y - state.startCursorPos.y;

    // Calculate live positions by applying delta to original positions
    const livePositions = new Map<string, { x: number; y: number }>();

    state.originalPositions.forEach((original, pointId) => {
      livePositions.set(pointId, {
        x: original.x + totalDx,
        y: original.y + totalDy,
      });
    });

    set({
      liveEditingPositions: livePositions,
    });

    // Broadcast coordinated drag update event
    try {
      document.dispatchEvent(
        new CustomEvent('coordinated:drag-update', {
          detail: {
            offset: { x: totalDx, y: totalDy },
            draggedPoints: Array.from(state.originalPositions.entries()).map(
              ([pointId, original]) => ({
                pointId,
                assemblyId: original.assemblyId,
                startX: original.x,
                startY: original.y,
                currentX: original.x + totalDx,
                currentY: original.y + totalDy,
              })
            ),
            cursorPos,
          },
        })
      );
    } catch {}
  },

  endDrag: () => {
    const state = get();
    if (!state.isDragging) return [];

    // Calculate final positions using the live editing positions
    const finalPositions: Array<{
      pointId: string;
      assemblyId: string;
      finalX: number;
      finalY: number;
    }> = [];

    state.liveEditingPositions.forEach((livePos, pointId) => {
      const original = state.originalPositions.get(pointId);
      if (original) {
        finalPositions.push({
          pointId,
          assemblyId: original.assemblyId,
          finalX: Math.round(livePos.x),
          finalY: Math.round(livePos.y),
        });
      }
    });

    // Clean reset - no transition state needed
    set({
      isDragging: false,
      dragMode: null,
      startCursorPos: { x: 0, y: 0 },
      originalPositions: new Map(),
      liveEditingPositions: new Map(),
      liveEditingPointId: null,
    });

    // Broadcast coordinated drag end event
    try {
      document.dispatchEvent(
        new CustomEvent('coordinated:drag-end', {
          detail: {
            finalPositions,
            draggedPoints: finalPositions,
          },
        })
      );
    } catch {}

    return finalPositions;
  },

  cancelDrag: () => {
    set({
      isDragging: false,
      dragMode: null,
      startCursorPos: { x: 0, y: 0 },
      originalPositions: new Map(),
      liveEditingPositions: new Map(),
      liveEditingPointId: null,
    });
  },

  getPointPosition: pointId => {
    const state = get();

    // If we're dragging and have live position, return it
    if (state.isDragging && state.liveEditingPositions.has(pointId)) {
      return state.liveEditingPositions.get(pointId) || null;
    }

    return null;
  },

  isDraggingPoint: pointId => {
    const state = get();
    return state.isDragging && state.originalPositions.has(pointId);
  },

  setLiveEditingPoint: (
    pointId: string | null,
    positions?: Map<string, { x: number; y: number }>
  ) => {
    set({
      liveEditingPointId: pointId,
      liveEditingPositions: positions || new Map(),
    });
  },
}));
